// src/components/payout/PayoutMethodsList.jsx
import React, { memo, useCallback, useState } from 'react';
import { Banknote, CheckCircle, AlertTriangle, Copy, Check, Trash2 } from 'lucide-react';

const PayoutMethodsList = memo(({
  payoutMethods,
  processing,
  deletingId,
  showDeleteConfirm,
  onSetDefault,
  onDelete,
  onShowDeleteConfirm,
  copiedField,
  onCopyToClipboard
}) => {
  const formatAccountNumber = useCallback((accountNumber) => {
    if (!accountNumber) return '';
    const cleaned = accountNumber.replace(/\D/g, '');
    if (cleaned.length !== 10) return accountNumber;
    return `${cleaned.substring(0, 4)}-${cleaned.substring(4, 8)}-${cleaned.substring(8, 10)}`;
  }, []);

  return (
    <div className="space-y-4">
      {/* Payout Methods List */}
      {payoutMethods.length > 0 ? (
        <div className="space-y-3">
          <h3 className="font-bold text-black text-sm">Your Bank Accounts</h3>
          {payoutMethods.map((method) => (
            <PayoutMethodCard
              key={method.payout_method_id}
              method={method}
              formatAccountNumber={formatAccountNumber}
              processing={processing}
              deletingId={deletingId}
              showDeleteConfirm={showDeleteConfirm}
              onSetDefault={onSetDefault}
              onDelete={onDelete}
              onShowDeleteConfirm={onShowDeleteConfirm}
              copiedField={copiedField}
              onCopyToClipboard={onCopyToClipboard}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm p-6 text-center">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Banknote className="w-6 h-6 text-gray-400" />
          </div>
          <h4 className="text-sm font-medium mb-2">No bank accounts</h4>
          <p className="text-xs text-gray-600 mb-4">
            Add a bank account to receive payments
          </p>
        </div>
      )}
    </div>
  );
});

const PayoutMethodCard = memo(({
  method,
  formatAccountNumber,
  processing,
  deletingId,
  showDeleteConfirm,
  onSetDefault,
  onDelete,
  onShowDeleteConfirm,
  copiedField,
  onCopyToClipboard
}) => {
  const [localConfirm, setLocalConfirm] = useState(false);

  const handleDeleteClick = useCallback(() => {
    if (showDeleteConfirm === method.payout_method_id) {
      onDelete(method.payout_method_id);
    } else {
      onShowDeleteConfirm(method.payout_method_id);
    }
  }, [method.payout_method_id, onDelete, onShowDeleteConfirm, showDeleteConfirm]);

  const handleCopy = useCallback(() => {
    onCopyToClipboard(method.details.account_number, `account-${method.payout_method_id}`);
  }, [method, onCopyToClipboard]);

  return (
    <div className="bg-white rounded-xl shadow-sm p-4">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg flex items-center justify-center">
          <Banknote className="w-5 h-5 text-blue-600" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-medium text-sm">{method.details.bank_name || 'Bank Account'}</h4>
            {method.is_default === 1 && (
              <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full">
                Default
              </span>
            )}
            {method.is_verified === 1 && (
              <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                Verified
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <p className="text-xs text-gray-600">
              {formatAccountNumber(method.details.account_number)} • {method.details.account_name}
            </p>
            <button
              onClick={handleCopy}
              className="p-0.5 hover:bg-gray-100 rounded"
            >
              {copiedField === `account-${method.payout_method_id}` ? (
                <Check className="w-3 h-3 text-green-600" />
              ) : (
                <Copy className="w-3 h-3 text-gray-500" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Verification Status */}
      {method.verification_status && !method.verification_status.matches_profile && (
        <div className="p-2 rounded text-xs mb-3 bg-yellow-50 text-yellow-700">
          <div className="flex items-center">
            <AlertTriangle className="w-3 h-3 mr-1" />
            <span>Name doesn't match profile</span>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 pt-3 border-t">
        {method.is_default !== 1 && (
          <button
            onClick={() => onSetDefault(method.payout_method_id)}
            disabled={processing}
            className="flex-1 text-xs text-[#ff660e] font-medium py-2 border border-[#ff660e] rounded-lg hover:bg-orange-50 disabled:opacity-50"
          >
            Set Default
          </button>
        )}
        <button
          onClick={() => onShowDeleteConfirm(method.payout_method_id)}
          disabled={deletingId === method.payout_method_id}
          className="flex-1 text-xs text-red-600 font-medium py-2 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
        >
          {deletingId === method.payout_method_id ? 'Deleting...' : 'Remove'}
        </button>
      </div>

      {/* Delete Confirmation */}
      {showDeleteConfirm === method.payout_method_id && (
        <DeleteConfirmation
          onConfirm={() => onDelete(method.payout_method_id)}
          onCancel={() => onShowDeleteConfirm(null)}
        />
      )}
    </div>
  );
});

const DeleteConfirmation = memo(({ onConfirm, onCancel }) => (
  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
    <div className="flex items-start space-x-2">
      <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
      <div className="flex-1">
        <p className="text-xs font-medium text-red-800 mb-1">Delete this account?</p>
        <p className="text-xs text-red-700 mb-2">
          This action cannot be undone.
        </p>
        <div className="flex gap-2">
          <button
            onClick={onConfirm}
            className="flex-1 text-xs bg-red-600 text-white py-1.5 rounded"
          >
            Yes, Delete
          </button>
          <button
            onClick={onCancel}
            className="flex-1 text-xs border border-gray-300 py-1.5 rounded hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
));

export default PayoutMethodsList;